import {TableData} from "@common/SortableTable";
import {dateRange, FilterOption} from "@common/FilterComponent";
import {get, isEqual} from "lodash-es";
import {User} from "@/api/dto/dto";
import {AxiosError} from "axios";
import {useAlertStore} from "@/store/alert_store";
import {ToastType} from "patient-ping-remedy/packages/toast";
import {log} from "@/helpers/logger";

const Helpers = {
  isDefined<T>(value: T | null | undefined) : value is T {
    return !(value === undefined || value === null);
  },
  isEmpty(value: string) : boolean {
    return value === "";
  },
  isEmptyObject(obj: object) : boolean {
    return Object.keys(obj).length === 0;
  },
  isEmptyArray(arr: any[]) : boolean {
    return arr.length === 0;
  },
  isEmptyUnknown(value: unknown) : boolean {
    if (!this.isDefined(value)) {
      return true;
    } else if (Array.isArray(value)) {
      return this.isEmptyArray(value);
    } else if (typeof value === 'object' && value !== null) {
      return this.isEmptyObject(value);
    } else if (typeof value === 'string') {
      return this.isEmpty(value);
    } else {
      return !value;
    }
  },
  calculateTimeDifference(startDate : Date, endDate : Date) {
    const diffTimezone = (startDate.getTimezoneOffset() - endDate.getTimezoneOffset()) * 1000 * 60;
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime() + diffTimezone);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  },
  calculateDaysHoursMinutesTimeDiff(startDate : Date, endDate : Date) {
    let diff = Math.abs(endDate.getTime() - startDate.getTime());
    const totalHours = Math.floor(diff / (1000 * 60 * 60));

    const days = Math.floor((((diff / 1000) / 60) /60) / 24);
    diff = diff - (days * 1000 * 60 * 60 * 24);
    const hours = Math.floor(((diff / 1000) / 60) / 60);
    diff = diff - (hours * 1000 * 60 * 60);
    const minutes = Math.floor(((diff / 1000) / 60));
    return {
      days, hours, minutes, totalHours
    };
  },
  calculateSecondsToDaysHrsMinsSecs(seconds: number) {
    const days = Math.floor(seconds / (3600*24));
    const hours = Math.floor(seconds % (3600*24) / 3600);
    const minutes = Math.floor(seconds % 3600 / 60);
    const secs = Math.floor(seconds % 60);
    return {
      days,
      hours,
      minutes,
      secs,
      daysDisplay: days > 0 ? days + (days === 1 ? " day" : " days") : "",
      hoursDisplay: hours > 0 ? hours + (hours === 1 ? " hr" : " hrs") : "",
      minutesDisplay: minutes > 0 ? minutes + " min" : "",
      secondsDisplay: seconds + (seconds === 1 ? " second" : " seconds")
    };
  },
  sortByString(data: TableData[], key: string, sortOrder: string) : any[] {
    return data.sort((a: any, b: any) => {
      if (sortOrder === 'asc') {
        return a[key].value.localeCompare(b[key].value);
      } else {
        return b[key].value.localeCompare(a[key].value);
      }
    });
  },
  sortByNumber(data: TableData[], key: string, sortOrder: string) : any[] {
    return data.sort((a: any, b: any) => {
      if (sortOrder === 'asc') {
        return a[key].value - b[key].value;
      } else {
        return b[key].value - a[key].value;
      }
    });
  },
  sortByDate(data: TableData[], key: string, sortOrder: string) : any[] {
    return data.sort((a: any, b: any) => {
      if (sortOrder === 'asc') {
        return new Date(a[key].value).valueOf() - new Date(b[key].value).valueOf();
      } else {
        return new Date(b[key].value).valueOf() - new Date(a[key].value).valueOf();
      }
    });
  },
  sortByCurrentUser(data: TableData[], key: string, sortOrder: string, currentUser: User | null | undefined) {
    if (!currentUser) {
      return data;
    }

    const compare = (a: TableData, b: TableData): number => {
      const aValues = (a[key]?.value ?? []) as string[];
      const bValues = (b[key]?.value ?? []) as string[];

      const aHasCurrent = aValues.includes(currentUser.id);
      const bHasCurrent = bValues.includes(currentUser.id);

      if (aHasCurrent && !bHasCurrent) return -1;
      if (!aHasCurrent && bHasCurrent) return 1;

      const aEmpty = aValues.length === 0;
      const bEmpty = bValues.length === 0;

      if (aEmpty && !bEmpty) return 1;
      if (!aEmpty && bEmpty) return -1;

      return 0;
    };

    return data.sort((a, b) => {
      const result = compare(a, b);
      return sortOrder === "asc" ? result : -result;
    });
  },
  sort(data: TableData[], type: 'string' | 'number' | 'date' | 'array' | 'custom', key: string, sortOrder: string) : any[] {
    if(type === 'number') {
      return this.sortByNumber(data, key, sortOrder);
    } else if(type === 'date') {
      return this.sortByDate(data, key, sortOrder);
    } else {
      return this.sortByString(data, key, sortOrder);
    }
  },
  search(searchTerm: string, data: any[], searchKeys: string[]) : any[] {
    return data.filter((item: any) => {
      return searchKeys.some((key: string) => {
        return item[key].value.toString().toLowerCase().includes(searchTerm.toLowerCase());
      });
    });
  },
  filter(
    filters: Record<string, any>,
    previousFilters: Record<string, any>,
    data: TableData[], 
    filterOptions: FilterOption[]
  ): TableData[] {

    const filterKeys = Object.keys(filters);
    const previousFilterKeys = Object.keys(previousFilters);
    const removedFilterKeys = previousFilterKeys.filter((filterKey) => !filterKeys.includes(filterKey));

    let newTableData = [...data];
    let reasonToRefetch = false;

    for (const filterKey of filterKeys) {
      const filterOption = filterOptions.find((option) => option.key === filterKey);
      const activeFilters = Object.values(filters[filterKey as keyof typeof filters]);
      const previousActiveFilters = Object.values(previousFilters[filterKey as keyof typeof previousFilters] ?? {});
      const filterLabels = activeFilters
        .filter((value: any) => value && value.label)
        .map((value: any) => value.label);

      const filterValues = activeFilters
        .filter((value: any) => Helpers.isDefined(value) && Helpers.isDefined(value.value))
        .map((value: any) => value.value);

      if (filterOption?.dataType === 'array') {
        if(filterOption.type === 'date-range') {
          newTableData = newTableData.filter((item) => {
            const itemDate = new Date(item[filterKey].value as string);
            itemDate.setHours(0,0,0,0);
            const itemTime = itemDate.getTime();

            const { startDate, endDate } = filters[filterKey as keyof typeof filters];

            const startTime = startDate ? new Date(startDate).getTime() : null;
            const endTime = endDate ? new Date(endDate).getTime() : null;

            if (startTime && endTime) {
              return itemTime >= startTime && itemTime <= endTime;
            } else if (startTime) {
              return itemTime >= startTime;
            } else if (endTime) {
              return itemTime <= endTime;
            }
            return true;
          });

          continue;
        }

        newTableData = filterValues.length === 0 ? data : newTableData.filter((item) => {
          return filterValues.some((filterValue) => {
            const value = item[filterKey].value;
            const isEmptyMatch = Helpers.isEmpty(filterValue) && Helpers.isEmptyArray(value as []);
            if (typeof value === 'number') {
              return value === filterValue || isEmptyMatch;
            }
            return value.includes(filterValue) || isEmptyMatch;
          });
        });
      } else if (filterOption?.dataType === 'ajax') {
        if (filterOption?.type === 'date-range') {
          const currentDateRange = filters[filterKey as keyof typeof filters] as dateRange;
          const previousDateRange = previousFilters[filterKey as keyof typeof previousFilters] as dateRange;

          filterOption.query.params[filterKey] = currentDateRange;
          if (!reasonToRefetch) {
            reasonToRefetch = !isEqual(currentDateRange, previousDateRange);
          }
        } else {
          filterOption.query.params[filterKey] = filterValues;
          if (!reasonToRefetch) {
            reasonToRefetch = !isEqual(activeFilters, previousActiveFilters);
          }
        }
      } else {
        newTableData = newTableData.filter((item) => {
          return filterLabels.some((filterLabel) => {
            return item[filterKey].value === filterLabel;
          });
        });
      }
    }

    for (const filterKey of removedFilterKeys) {
      const filterOption = filterOptions.find((option) => option.key === filterKey);

      if (filterOption?.dataType === 'ajax') {
        filterOption.query.params[filterKey] = [];
        reasonToRefetch = true;
      }
    }

    if (reasonToRefetch) {
      for (const filterKey of [...filterKeys, ...previousFilterKeys]) {
        const filterOption = filterOptions.find((option) => option.key === filterKey);

        if (filterOption?.dataType === 'ajax') {
          filterOption.query.invalidate();
          break;
        }
      }
    }

    return newTableData;
  },
  extractFilterValues(filters: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {};
    for (const [key, value] of Object.entries(filters)) {
      if (Array.isArray(value)) {
        result[key] = value
          .filter((v: any) => Helpers.isDefined(v) && Helpers.isDefined(v.value))
          .map((v: any) => v.value);
      } else {
        result[key] = value;
      }
    }
    return result;
  },
  removeDuplicates<T>(array: T[], key: keyof T) {
    return Array.from(new Map(array.map(item => [item[key], item])).values());
  },
  joinWithComma(...parts: (string | undefined)[]) {
    return parts.filter(Boolean).join(', ');
  },
  traceId(error: AxiosError) {
    const traceId = error.response?.headers['x-trace-id'];
    return traceId ? `[Support code: ${traceId}]` : '';
  },
  backendErrorMessage(error: AxiosError): string | null {
    const data = error.response?.data;
    if (typeof data === 'string') {
      const trimmed = data.trim();
      if (trimmed.length === 0) return null;
      try {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed === 'string') return parsed;
      } catch {
        // Not JSON — fall through and use the raw string.
      }
      return trimmed;
    }
    if (data && typeof data === 'object' && typeof (data as {message?: unknown}).message === 'string') {
      return (data as {message: string}).message;
    }
    return null;
  },
  alertOnError(content: string, error: Error | string) {
    useAlertStore.getState().addAlert({content, type: ToastType.ERROR});
    log('error', error);
  },
  getAge(dobString: string): number {
    const dob = new Date(dobString);
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const monthDiff = now.getMonth() - dob.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
      age--;
    }

    return age;
  },
  findScrollParent(element: HTMLElement | null): HTMLElement | null {
    return !element
      ? null
      : element.scrollHeight > element.clientHeight
        ? element
        : Helpers.findScrollParent(element.parentElement);
  },
  getCategorizedElements<T>(elements: T[], property: string) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const todayElements: T[] = [];
    const yesterdayElements: T[] = [];
    const olderElements: T[] = [];

    elements.forEach((element) => {
      const eventDate = new Date(get(element, property));
      eventDate.setHours(0, 0, 0, 0);

      if (eventDate.getTime() === today.getTime()) {
        todayElements.push(element);
      } else if (eventDate.getTime() === yesterday.getTime()) {
        yesterdayElements.push(element);
      } else {
        olderElements.push(element);
      }
    });

    return {
      todayElements,
      yesterdayElements,
      olderElements,
    };
  },
  safeJsonParse(json: string, fallback = {}) {
    try {
      return JSON.parse(json);
    } catch(e) {
      log('error', 'JSON parse error', e);
      return fallback;
    }
  },
  isToday(selectedDate: Date | null) {
    if(!selectedDate) {
      return false;
    }
    const today = new Date();

    return (
      selectedDate.getFullYear() === today.getFullYear() &&
    selectedDate.getMonth() === today.getMonth() &&
    selectedDate.getDate() === today.getDate()
    );
  },
  isDateInPast(date: Date | null): boolean {
    if (!date) return false;

    const today = new Date();

    today.setHours(0, 0, 0, 0);

    const inputDate = new Date(date);
    inputDate.setHours(0, 0, 0, 0);

    return inputDate < today;
  },
  expandFilterKeys(filters: Record<string, unknown>): string[] {
    return Object.entries(filters).flatMap(([key, value]) => {
      if (Array.isArray(value)) {
        return value.length > 0 ? [key] : [];
      }
      if (value && typeof value === 'object') {
        // Expand date range objects (e.g. dueDate -> startDate, endDate)
        return Object.entries(value)
          .filter(([_, v]) => !!v)
          .map(([subKey]) => subKey);
      }
      return value ? [key] : [];
    });
  },
  isMinor(dateOfBirth: string): boolean {
    const dob = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age < 18;
  }
};

export default Helpers;
